import { api } from './api-client';

/**
 * The interest vocabulary, as `/v1/admin/interests` serves it.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/interests/`.
 *
 * There is no app-facing counterpart to this. The mobile app is handed the
 * interests already attached to each headline it is offered and never asks what
 * the vocabulary is — docs/onboarding.md is explicit that a learner is shown
 * headlines rather than categories. This portal is the only place they exist as
 * a list, which is also why the tagging has to be right here.
 */
export interface Interest {
  id: string;
  /** A stable slug. Events carry this, not the label. */
  code: string;
  /** What this portal shows. Never seen by a learner. */
  label: string;
  createdAt: string;
  updatedAt: string;
}

/** What the add and edit forms produce. */
export interface InterestDraft {
  code: string;
  label: string;
}

export async function listInterests(): Promise<Interest[]> {
  const { interests } = await api.get<{ interests: Interest[] }>(
    '/admin/interests',
  );
  return interests;
}

export async function createInterest(draft: InterestDraft): Promise<Interest> {
  const { interest } = await api.post<{ interest: Interest }>(
    '/admin/interests',
    draft,
  );
  return interest;
}

export async function updateInterest(
  id: string,
  draft: InterestDraft,
): Promise<Interest> {
  const { interest } = await api.patch<{ interest: Interest }>(
    `/admin/interests/${id}`,
    draft,
  );
  return interest;
}

export function deleteInterest(id: string): Promise<void> {
  return api.del(`/admin/interests/${id}`);
}
